import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import type { ABTest, ContentType } from "@/types/ab-tests";
import { getBulletIndex } from "@/utils/ab-test-utils";

interface VariantCardsProps {
  variantA: ABTest["variantA"];
  variantB: ABTest["variantB"];
  testedFields: ContentType[];
  showIntent: boolean | string;
  intentFrom: string;
  intentTo: string;
  getFieldContent: (variant: ABTest["variantA"], field: ContentType) => string;
  onCopyVariantB: () => void;
}

export function VariantCards({
  variantA,
  variantB,
  testedFields,
  showIntent,
  intentFrom,
  intentTo,
  getFieldContent,
  onCopyVariantB,
}: VariantCardsProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <Card className="space-y-3 p-4">
        <h4 className="text-base font-semibold text-foreground">Variant A</h4>
        {showIntent && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Intent</p>
            <p className="text-sm text-foreground">{intentFrom}</p>
          </div>
        )}
        {testedFields.map((field) => {
          const idx = getBulletIndex(field);
          const label = field === "title" ? "Title" : `Bullet ${(idx ?? 0) + 1}`;
          return (
            <div key={field} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
              <p className="text-xs leading-relaxed text-foreground">{getFieldContent(variantA, field)}</p>
            </div>
          );
        })}
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold text-foreground">Variant B</h4>
          <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-foreground" onClick={onCopyVariantB}>
            <Copy className="h-3 w-3 mr-0.5" />
            Copy
          </Button>
        </div>
        {showIntent && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Intent</p>
            <p className="text-sm text-foreground">{intentTo}</p>
          </div>
        )}
        {testedFields.map((field) => {
          const idx = getBulletIndex(field);
          const label = field === "title" ? "Title" : `Bullet ${(idx ?? 0) + 1}`;
          return (
            <div key={field} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
              <p className="text-xs leading-relaxed text-foreground">{getFieldContent(variantB, field)}</p>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
